/**
 * Realistic pixel art for the painted ground: each material (meadow, dry grass, earth, sand,
 * stone, moss, sea, paddy water) has its own hue-shifted ramp, and every pixel picks one material
 * and one step of its ramp — lit from up-screen, dithered only between neighbouring steps — so
 * edges stay crisp instead of blending into mud. Grass grows in clumps with volume that shade the
 * earth beside them.
 */
import { hash2, valueNoise } from '@nine/shared';

export type RGB = [number, number, number];

const ramp = (...hex: string[]): readonly RGB[] =>
  hex.map((h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as RGB);

/** Every material's ramp, deep shadow (leaning blue) to full sun (leaning yellow). */
export const RAMPS = {
  lush: ramp('#1f3a2a', '#2c5132', '#3e6b38', '#56873f', '#74a24a', '#9cbd5c'),
  short: ramp('#3b4227', '#545c30', '#6f783b', '#8c9447', '#aaae58', '#cac872'),
  dirt: ramp('#3a2a22', '#533c2c', '#6e5038', '#8a6847', '#a6845c', '#c2a178'),
  sand: ramp('#8f7a58', '#ab9670', '#c6b088', '#dccaa2', '#ece0bf'),
  wet: ramp('#5e5040', '#76674f', '#8e7f63', '#a69878'),
  stone: ramp('#3c3a3e', '#565358', '#726d6c', '#8f8981', '#aca496', '#c8bfae'),
  moss: ramp('#2e4630', '#46653a', '#62844a'),
  sea: ramp('#18294a', '#1f3a63', '#284f7d', '#326894', '#3f83a6', '#5aa0b4', '#7fbcbe', '#a9d6cc'),
  foam: ramp('#cfe4e2', '#f0f8f4'),
  paddy: ramp('#3e6466', '#557f7c', '#719c94', '#92b8b0'),
} as const;
const R = RAMPS;

const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const bayer = (x: number, y: number): number => (BAYER[(y & 3) * 4 + (x & 3)]! + 0.5) / 16;
const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (a: number, b: number, v: number): number => {
  const t = clamp01((v - a) / (b - a));
  return t * t * (3 - 2 * t);
};

/** A step of a ramp: `l` 0 (deep shadow) .. 1 (full sun), dithered between neighbouring steps. */
function snap(r: readonly RGB[], l: number, x: number, y: number, dither = 0.55): RGB {
  const i = Math.round(clamp01(l) * (r.length - 1) + (bayer(x, y) - 0.5) * dither);
  return r[i < 0 ? 0 : i >= r.length ? r.length - 1 : i]!;
}

interface Cell {
  d1: number;
  d2: number;
  id: number;
  dx: number;
  dy: number;
}
/** Nearest and second-nearest jittered cell points (Voronoi): clumps, stones. */
function cells(qx: number, qy: number): Cell {
  const ix = Math.floor(qx);
  const iy = Math.floor(qy);
  const c: Cell = { d1: 9, d2: 9, id: 0, dx: 0, dy: 0 };
  for (let b = -1; b <= 1; b++)
    for (let a = -1; a <= 1; a++) {
      const cx = ix + a;
      const cy = iy + b;
      const px = cx + 0.15 + hash2(cx * 17, cy * 31) * 0.7;
      const py = cy + 0.15 + hash2(cx * 7, cy * 13) * 0.7;
      const d = Math.hypot(qx - px, qy - py);
      if (d < c.d1) {
        c.d2 = c.d1;
        c.d1 = d;
        c.id = hash2(cx * 5 + 3, cy * 11 + 7);
        c.dx = qx - px;
        c.dy = qy - py;
      } else if (d < c.d2) c.d2 = d;
    }
  return c;
}

/** What the field painter needs to know about the map and the camera. */
export interface FieldView {
  /** Ground pixel → view grid. */
  grid(sx: number, sy: number): [number, number];
  /** View grid → map grid, so clumps stay put when the camera turns. */
  unturn(x: number, y: number): [number, number];
  /** The map-space directions of the view's grid axes (to light clumps from up-screen). */
  axes: readonly [readonly [number, number], readonly [number, number]];
  /** Grass cover 0..1 at a map point. */
  cover(ux: number, uy: number): number;
  /** How lush (0, dry) .. (1, meadow) the grass is at a map point. */
  lush(ux: number, uy: number): number;
  /** Sunlight on the swells at a view point (about -1..1). */
  sun(gx: number, gy: number): number;
}

/** Clumps per tile (each way). */
const CLUMP = 2.8;
function clumpAt(fv: FieldView, ux: number, uy: number): Cell | null {
  const v = cells(ux * CLUMP, uy * CLUMP);
  return v.id < fv.cover(ux, uy) * 1.08 ? v : null;
}
const clumpRamp = (fv: FieldView, v: Cell, ux: number, uy: number): readonly RGB[] =>
  hash2(Math.floor(v.id * 1e6), 13) < fv.lush(ux, uy) * 1.15 ? R.lush : R.short;

/**
 * How far a point `(dx, dy)` (map) from a dome's middle lies away from the key light, which stands
 * up and to the right on screen (−y on the view grid, a little −x): negative on the lit side.
 */
const fromSun = (dx: number, dy: number, [ex, ey]: FieldView['axes']): number =>
  0.45 * (dx * ex[0] + dy * ex[1]) + 1.35 * (dx * ey[0] + dy * ey[1]);

/** Bare earth: lit on the swells, gritty, with pebbles that have a sunny top and a shadow. */
export function earthPixel(x: number, y: number, l: number): RGB {
  l += (valueNoise(x / 5, y / 2.5) - 0.5) * 0.22;
  const cx = Math.floor(x / 7);
  const cy = Math.floor(y / 5);
  const h = hash2(cx * 19, cy * 23);
  if (h < 0.09) {
    const px = cx * 7 + 1 + Math.floor(hash2(cx, cy * 3) * 4);
    const py = cy * 5 + 1 + Math.floor(hash2(cy, cx * 5) * 2);
    const wide = h < 0.03 ? 3 : 2;
    const [dx, dy] = [x - px, y - py];
    if (dx >= 0 && dx < wide) {
      if (dy === 0) return snap(R.stone, 0.85, x, y, 0);
      if (dy === 1) return snap(R.stone, wide === 3 ? 0.45 : 0.35, x, y, 0);
      if (dy === 2) return R.dirt[0]!;
    }
  }
  if (hash2(x * 5 + 1, y * 3) > 0.985) l -= 0.3; // grit
  return snap(R.dirt, l, x, y);
}

/** Grass is painted in blocks this many pixels across: coarser than the earth and stones. */
const GRASS_BLOCK = 2;

/**
 * The countryside: grass clumps with volume, or the earth between them. `grass` says which.
 * The grass comes in blocks of `GRASS_BLOCK` pixels, each the colour at its corner; the earth
 * keeps every pixel, so pebbles and grit stay sharp.
 */
export function fieldPixel(
  fv: FieldView,
  x: number,
  y: number,
  gx: number,
  gy: number,
): { c: RGB; grass: boolean } {
  const bx = x - (((x % GRASS_BLOCK) + GRASS_BLOCK) % GRASS_BLOCK);
  const by = y - (((y % GRASS_BLOCK) + GRASS_BLOCK) % GRASS_BLOCK);
  const corner = bx === x && by === y;
  const block = corner ? fieldAt(fv, x, y, gx, gy) : fieldAt(fv, bx, by, ...fv.grid(bx + 0.5, by + 0.5));
  if (block.grass) return block;
  const own = corner ? block : fieldAt(fv, x, y, gx, gy);
  return own.grass ? { c: bareEarth(fv, x, y, gx, gy), grass: false } : own;
}

function fieldAt(fv: FieldView, x: number, y: number, gx: number, gy: number): { c: RGB; grass: boolean } {
  const [ux, uy] = fv.unturn(gx, gy);
  const sun = fv.sun(gx, gy);
  const v = clumpAt(fv, ux, uy);
  if (v) {
    // Domed: the side of the clump facing the key light (up and to the right) catches the sun.
    let l = 0.5 - fromSun(v.dx, v.dy, fv.axes) * 0.6 + sun * 0.45;
    if (v.d2 - v.d1 < 0.06) l -= 0.32; // the crevice between two clumps
    const col = hash2(x, 91);
    const blade = hash2(x * 3 + 1, Math.floor((y + col * 3) / 3));
    if (blade > 0.78) l += (y + Math.floor(col * 3)) % 3 === 0 ? 0.24 : 0.12;
    else if (blade < 0.18) l -= 0.14;
    return { c: snap(clumpRamp(fv, v, ux, uy), l, x, y), grass: true };
  }
  // Blades of the clump just in front (lower on screen) poking up over the bare earth.
  for (let up = 1; up <= 3; up++) {
    const [wx, wy] = fv.unturn(...fv.grid(x + 0.5, y + 0.5 + up));
    const w = clumpAt(fv, wx, wy);
    if (w && hash2(x * 7, Math.floor(w.id * 1e5)) < 0.55 - up * 0.12)
      return { c: clumpRamp(fv, w, wx, wy)[up === 1 ? 3 : 4]!, grass: true };
  }
  return { c: bareEarth(fv, x, y, gx, gy), grass: false };
}

/** Earth between the clumps, in the shadow a clump up-sun (up and to the right) casts across it. */
function bareEarth(fv: FieldView, x: number, y: number, gx: number, gy: number): RGB {
  const [sx, sy] = fv.unturn(...fv.grid(x + 1, y - 1));
  const shade = clumpAt(fv, sx, sy) ? 0.3 : 0;
  return earthPixel(x, y, 0.55 + fv.sun(gx, gy) * 0.55 - shade);
}

/** The earthen dikes between paddies: packed mud with a grassy crest. */
export function dikePixel(x: number, y: number, sun: number): RGB {
  if (hash2(x, y >> 1) > 0.62) return snap(R.short, y & 1 ? 0.4 : 0.7, x, y, 0);
  return earthPixel(x, y, 0.5 + sun * 0.4);
}

/** Beach `shore` tiles from the water: dry sand combed into ripples, wet sand at the edge, the tide line. */
export function sandPixel(x: number, y: number, shore: number, sun: number): RGB {
  const wet = smooth(0.7, 0.15, shore);
  const r = wet > bayer(x >> 1, y >> 1) ? R.wet : R.sand;
  const rip = ((x * 0.1 + y * 0.95 + valueNoise(x / 22, y / 11) * 7) / 3.2) % 1;
  const l = 0.6 + sun * 0.25 + (rip < 0.22 ? 0.22 : rip < 0.42 ? -0.16 : 0) * (1 - wet);
  if (Math.abs(shore - 1 - (valueNoise(x / 15, 7) - 0.5) * 0.5) < 0.05 && hash2(x, y) > 0.6) return R.wet[1]!;
  if (hash2(x * 7 + 1, y * 3) > 0.993) return R.sand[4]!; // a shell
  if (hash2(x * 7 + 1, (y - 1) * 3) > 0.993) return R.wet[0]!; // …and its shadow
  return snap(r, l, x, y);
}

/**
 * Field and beach together: `shore` tiles from the sea. On the dunes the grass holds out in
 * clumps while the earth between turns to sand.
 */
export function countryPixel(
  fv: FieldView,
  x: number,
  y: number,
  gx: number,
  gy: number,
  shore: number,
): RGB {
  const sk = clamp01((2.6 - shore) / 0.8);
  if (sk >= 1) return sandPixel(x, y, shore, fv.sun(gx, gy));
  const f = fieldPixel(fv, x, y, gx, gy);
  if (sk <= 0) return f.c;
  return f.grass && sk < 0.6 + (bayer(x, y) - 0.5) * 0.4 ? f.c : sandPixel(x, y, shore, fv.sun(gx, gy));
}

/** A cart track `d` tiles from its middle: ruts with a thrown-up lip, bedded stones, a grassy crown and verge. */
export function roadPixel(fv: FieldView, x: number, y: number, gx: number, gy: number, d: number): RGB {
  if (d > 0.82 + valueNoise(x / 5 + 7, y / 2.5) * 0.3) {
    const f = fieldPixel(fv, x, y, gx, gy);
    if (f.grass) return f.c;
  }
  const [ux, uy] = fv.unturn(gx, gy);
  const s = cells(ux * 3.4 + 50, uy * 3.4);
  if (s.id < 0.16 && s.d1 < 0.17) {
    return snap(R.stone, 0.55 - fromSun(s.dx, s.dy, fv.axes) * 2.6, x, y, 0.3);
  }
  let l = 0.6 + fv.sun(gx, gy) * 0.3 + (valueNoise(x / 6, y / 3) - 0.5) * 0.18;
  const rut = Math.abs(d - 0.5);
  if (rut < 0.05) l -= 0.42;
  else if (rut < 0.12) l -= 0.25;
  else if (rut < 0.19) l += 0.14;
  if (d < 0.2 && valueNoise(x / 4, y / 2) > 0.52)
    return snap(R.short, 0.45 + (hash2(x, y >> 1) > 0.7 ? 0.25 : 0), x, y);
  if (hash2(x * 7, y + 3) > 0.985) l += 0.3;
  return snap(R.dirt, l, x, y);
}

/**
 * The village square, `(dx, dy)` from its middle (view grid) and `r` across: flagstones laid in
 * staggered courses, bevelled, a few cracked, moss in the joints, and a kerb of cut blocks.
 */
export function plazaPixel(
  x: number,
  y: number,
  gx: number,
  gy: number,
  dx: number,
  dy: number,
  r: number,
): RGB {
  const pd = Math.hypot(dx, dy);
  if (pd > r - 0.38) {
    if ((Math.atan2(dy, dx) * 3.5 + 20) % 1 < 0.07) return R.stone[0]!;
    return snap(R.stone, pd > r - 0.1 ? 0.2 : pd < r - 0.3 ? 0.85 : 0.55, x, y, 0.3);
  }
  const row = Math.floor(gy * 1.6);
  const u = gx * 1.25 + hash2(row, 3) * 3;
  const id = hash2(Math.floor(u), row * 7);
  const fu = ((u % 1) + 1) % 1;
  const fv = (((gy * 1.6) % 1) + 1) % 1;
  if (fu < 0.055 || fv < 0.09)
    return pd > 1.6 && hash2(x * 5, y) > 0.45 ? snap(R.moss, hash2(x, y * 3), x, y, 0) : R.stone[0]!;
  const wear = smooth(r - 0.6, 0.6, pd);
  let l = 0.42 + id * 0.22 + wear * 0.14 + (valueNoise(x / 3 + id * 9, y / 1.5) - 0.5) * 0.16;
  // Bevels: lit along the edge facing the key light (up-right on screen), dark along the one
  // facing away; the other two, side-on to it, only a step either way.
  if (fv < 0.19) l += 0.2;
  else if (fv > 0.87) l -= 0.22;
  else if (fu > 0.93) l += 0.08;
  else if (fu < 0.12) l -= 0.1;
  if (id < 0.2 && Math.abs(fv - 0.3 - fu * 0.5 - id) < 0.035) return R.stone[1]!; // a crack
  return snap(R.stone, l, x, y);
}

/** The sea `d` tiles out from the beach: foam at the edge, a line of surf, banded depths and ripples. */
export function seaPixel(x: number, y: number, d: number): RGB {
  if (d < 0.2 + valueNoise(x / 7, 3) * 0.16 && hash2(x, y) > 0.2) return snap(R.foam, 1 - d * 3, x, y);
  const surf = 0.95 + (valueNoise(x / 18, y / 6) - 0.5) * 0.5;
  if (Math.abs(d - surf) < 0.045 + valueNoise(x / 5, y / 3) * 0.05 && hash2(x * 3, y) > 0.3)
    return R.foam[0]!;
  let l = 1 - clamp01(d / 3.4) * 0.92;
  const wave = valueNoise(x / 16 + 2, y / 1.6);
  if (wave > 0.7) l += 0.14;
  else if (wave < 0.24) l -= 0.08;
  if (valueNoise(x / 46 + 3, y / 11) > 0.66) l += 0.1; // the sky mirrored in a calm band
  return snap(R.sea, l, x, y, 0.7);
}

/** A flooded paddy: rows of shoots (`fx` across the tile), their reflections, and still water mirroring the sky. */
export function paddyPixel(x: number, y: number, fx: number): RGB {
  const row = (fx * 4) % 1;
  if (row < 0.3) return snap(R.lush, [0.85, 0.6, 0.35][(y + Math.floor(hash2(x, 5) * 3)) % 3]!, x, y, 0);
  if (row < 0.44) return R.paddy[0]!;
  return snap(R.paddy, 0.45 + smooth(0.55, 0.8, valueNoise(x / 30, y / 8)) * 0.5, x, y);
}
